/**
 * Tenant Signup Form
 *
 * VERTICAL CLASSIFICATION — SOURCE OF TRUTH
 * The canonical list of valid `primary_vertical` values lives in the mothership
 * source repo. Local filesystem path (legacy folder name, not the live domain):
 *   c:/wamp64/www/indevithub.com/api/app/core/verticals.py — VERTICAL_MODULES
 * Live API: https://api.axoix.in
 *
 * This signup form is the upstream-most authoring surface for tenant
 * vertical classification: whatever the user picks here is stored
 * verbatim in tenants.settings.primary_vertical and propagated to the
 * tenant's companies.industry_type during provisioning.
 *
 * Rules:
 * 1. INDUSTRY_OPTIONS values below MUST be a subset of mothership
 *    VERTICAL_MODULES keys (plus "general" as fallback).
 * 2. The mothership validator rejects unknown values — do NOT add
 *    new options here without first adding the key in
 *    axoix.com/api/app/core/verticals.py VERTICAL_MODULES.
 * 3. NEVER add "barter" — it is the AI character / sales CRM module name, and
 *    the marketplace behind it stopped being provisioned on 2026-08-10.
 *
 *    CORRECTED 2026-08-20: this rule used to bracket "ecommerce" with "barter"
 *    and claim it "maps to the retail vertical". That was wrong, and the same
 *    wrong belief was independently encoded in three other places — the
 *    IndustryDetailsPage LEGACY_SLUG_REDIRECTS, an nginx 301, and the absence
 *    of any industry card. `ecommerce` is a first-class vertical in BOTH
 *    sources of truth (mothership VERTICAL_MODULES, POC VERTICAL_TABLES) and
 *    resolves through verticals_canonical.resolve. It is now offered below.
 * 4. SLUG_TO_VERTICAL (now in utils/verticalIntent.ts, shared with the
 *    industry pages that record the visitor's vertical intent) has
 *    right-hand-side values that must match an INDUSTRY_OPTIONS.value
 *    entry. Any orphan is a dead URL slug.
 */
import React, { Fragment, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "../../components/SEO/SEO";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import {
  checkSubdomain as checkSubdomainApi,
  validateSubdomain,
  checkApiHealth,
  sendSignupOtp,
  verifySignupOtp,
  requestResumeLink,
  RequestResumeRequest,
  // storefront state now comes from useSaleOpen(), not a private fetch
} from "../../services/api";
import { useSaleOpen } from "../../hooks/useSaleOpen";
import { companyToSubdomain } from "../../services/api/tenantService";
import { saveSignupDraft, loadSignupDraft, clearSignupDraft } from "../../utils/signupDraft";
import { validateReferralCode } from "../../services/api/partnerService";
import { useVerticalPricing } from "../../hooks/useVerticalPricing";
import { usePricingData } from "../../hooks/usePricingData";
import { getReferralCode } from "../../components/ReferralTracker/ReferralTracker";
import { track } from "../../services/analytics";
import { resolveVertical, getVerticalIntent } from "../../utils/verticalIntent";

// Colorful theme backgrounds
import pricingBg from "../../images/bg/pricing-bg.png";
import cardBg from "../../images/bg/pricing-bg01.png";

// Self-serve paid plans: Officer, Commander and Titan.
//
// Titan used to be sales-led — the pricing page showed CONTACT SALES because
// pricing_tiers.monthly_price was NULL and Titan sold only on 5/10-year
// terms. That changed 2026-09-08 (see PLAN-pricing-redesign.md): Titan now
// carries monthlyPrice/quarterlyPrice/sixMonthPrice/yearlyPrice like every
// other tier, priceOnRequest is false, and create-order/create-subscription
// in tenants.py and billing.py apply the same monthly/quarterly/six_month/
// yearly lookup to every tier with no Titan exclusion. Leaving this dropdown
// showing "Titan Enterprise — Contact Sales" after that shipped was the same
// class of defect as the frozen-price array below: the form disagreeing with
// what the Mothership actually sells.
//
// PRICES ARE NOT LISTED HERE, deliberately. They used to be, and every one of
// them was wrong: the dropdown quoted Officer Monthly at ₹4,999 while
// pricing_tiers said ₹1,500, Commander Yearly at ₹1,49,990 against ₹34,560 —
// roughly 3.3x across the board. The Mothership prices the order from
// pricing_tiers at create-order time and never trusts a number the client
// sends, so the customer was quoted one figure and charged another. That is
// the failure BR-101 asked us to stop repeating: render plan cards from the
// API, not from a frozen array.
//
// Only the shape of a plan lives here — which tier, which billing cycle, and
// how to label the cycle. The rupee figure comes from usePricingData(), the
// same hook /pricing renders from, so the two pages cannot disagree.
const PAID_PLAN_SHAPES: {
  value: string;
  tier: "officer" | "commander" | "titan";
  cycle: "monthly" | "quarterly" | "sixMonth" | "yearly";
  cycleLabel: string;
  suffix: string;
}[] = [
  { value: "officer", tier: "officer", cycle: "monthly", cycleLabel: "Monthly", suffix: "/mo" },
  { value: "officer_quarterly", tier: "officer", cycle: "quarterly", cycleLabel: "Quarterly", suffix: "/3mo" },
  { value: "officer_six_month", tier: "officer", cycle: "sixMonth", cycleLabel: "6-Month", suffix: "/6mo" },
  { value: "officer_yearly", tier: "officer", cycle: "yearly", cycleLabel: "Yearly", suffix: "/yr" },
  { value: "commander", tier: "commander", cycle: "monthly", cycleLabel: "Monthly", suffix: "/mo" },
  { value: "commander_quarterly", tier: "commander", cycle: "quarterly", cycleLabel: "Quarterly", suffix: "/3mo" },
  { value: "commander_six_month", tier: "commander", cycle: "sixMonth", cycleLabel: "6-Month", suffix: "/6mo" },
  { value: "commander_yearly", tier: "commander", cycle: "yearly", cycleLabel: "Yearly", suffix: "/yr" },
  { value: "titan", tier: "titan", cycle: "monthly", cycleLabel: "Monthly", suffix: "/mo" },
  { value: "titan_quarterly", tier: "titan", cycle: "quarterly", cycleLabel: "Quarterly", suffix: "/3mo" },
  { value: "titan_six_month", tier: "titan", cycle: "sixMonth", cycleLabel: "6-Month", suffix: "/6mo" },
  { value: "titan_yearly", tier: "titan", cycle: "yearly", cycleLabel: "Yearly", suffix: "/yr" },
];

// Indian States & UTs for dropdown
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  // Union Territories
  "Andaman & Nicobar", "Chandigarh", "Dadra Nagar Haveli & Daman Diu",
  "Delhi", "Jammu & Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

// GSTIN state code → state name (auto-fill when GSTIN entered)
const GSTIN_STATE_MAP: Record<string, string> = {
  "01": "Jammu & Kashmir", "02": "Himachal Pradesh", "03": "Punjab",
  "04": "Chandigarh", "05": "Uttarakhand", "06": "Haryana",
  "07": "Delhi", "08": "Rajasthan", "09": "Uttar Pradesh",
  "10": "Bihar", "11": "Sikkim", "12": "Arunachal Pradesh",
  "13": "Nagaland", "14": "Manipur", "15": "Mizoram",
  "16": "Tripura", "17": "Meghalaya", "18": "Assam",
  "19": "West Bengal", "20": "Jharkhand", "21": "Odisha",
  "22": "Chhattisgarh", "23": "Madhya Pradesh", "24": "Gujarat",
  "25": "Dadra Nagar Haveli & Daman Diu", "26": "Dadra Nagar Haveli & Daman Diu",
  "27": "Maharashtra", "29": "Karnataka", "30": "Goa",
  "32": "Kerala", "33": "Tamil Nadu", "34": "Puducherry",
  "35": "Andaman & Nicobar", "36": "Telangana", "37": "Andhra Pradesh",
  "38": "Ladakh",
};

interface SignupFormData {
  company_name: string;
  subdomain: string;
  admin_email: string;
  admin_password: string;
  admin_name: string;
  phone: string;
  plan: string;
  referral_code: string;
  primary_vertical: string;
  addon_verticals: string[];
  // Structured address
  state: string;
  city: string;
  pincode: string;
  address_line: string;
  address_line_2?: string;
  // GST/Tax Information (Optional)
  gstin: string;
}

// Industry vertical options shown in the signup dropdown.
// IMPORTANT: every `value` here must exist in mothership VERTICAL_MODULES
// at axoix.com/api/app/core/verticals.py (or be the "general" fallback).
// See top-of-file rules. The mothership validator rejects unknown values.
const INDUSTRY_OPTIONS = [
  { value: "general", label: "General Business", icon: "fa-building" },
  { value: "hotel", label: "Hotel & Hospitality", icon: "fa-hotel" },
  { value: "restaurant", label: "Restaurant & F&B", icon: "fa-utensils" },
  { value: "healthcare", label: "Healthcare & Clinic", icon: "fa-hospital" },
  { value: "education", label: "Education & Training", icon: "fa-graduation-cap" },
  { value: "travel", label: "Travel & Tourism", icon: "fa-plane" },
  { value: "logistics", label: "Logistics & Supply Chain", icon: "fa-truck" },
  { value: "realestate", label: "Real Estate", icon: "fa-home" },
  { value: "retail", label: "Retail & Distribution", icon: "fa-shopping-cart" },
  // Added 2026-08-20. `ecommerce` is a first-class vertical in BOTH sources
  // (mothership VERTICAL_MODULES, POC VERTICAL_TABLES) and resolves cleanly
  // through verticals_canonical.resolve — it was simply never offered here,
  // so /industry/ecommerce had no way to convert. `retail` above dropped
  // "& E-Commerce" from its label for the same reason: it had been covering
  // for a vertical that now stands on its own.
  { value: "ecommerce", label: "E-commerce", icon: "fa-shopping-bag" },
  { value: "manufacturing", label: "Manufacturing", icon: "fa-industry" },
  { value: "construction", label: "Construction", icon: "fa-hard-hat" },
  { value: "gov_contractor", label: "Govt Contractor", icon: "fa-landmark" },
  { value: "services", label: "Professional Services", icon: "fa-briefcase" },
  { value: "it_services", label: "IT Services & Software", icon: "fa-laptop-code" },
  { value: "consulting", label: "Consulting & Advisory", icon: "fa-user-tie" },
  { value: "trading", label: "Trading & Distribution", icon: "fa-exchange-alt" },
  { value: "car_dealer", label: "Car Dealer", icon: "fa-car" },
  { value: "bike_dealer", label: "Bike Dealer", icon: "fa-motorcycle" },
  { value: "salon", label: "Salon & Spa", icon: "fa-spa" },
  { value: "gym", label: "Gym & Fitness", icon: "fa-dumbbell" },
  { value: "arena", label: "Gaming Parlour & Snooker", icon: "fa-gamepad" },
  { value: "study_abroad", label: "Study Abroad Consultancy", icon: "fa-globe-americas" },
];

const isSelectableVertical = (value: string): boolean =>
  INDUSTRY_OPTIONS.some((opt) => opt.value === value);

/**
 * Which industry the form opens on, in falling order of confidence:
 *   1. ?vertical= on this URL — they clicked an industry CTA seconds ago.
 *   2. An explicit pick already saved in the draft. "general" does not count:
 *      it is the default value nobody actively chooses, and it used to beat a
 *      real browsing signal simply because the draft is written on first render.
 *   3. The industry cookie — the last industry page they read this month.
 *   4. General Business.
 */
/**
 * The SUBTYPE the visitor arrived with, if they named one.
 *
 * `resolveVertical("pg")` returns "hotel" — correct, because `hotel` is what
 * gets priced and provisioned, and the form's industry dropdown only offers
 * canonical verticals. But that resolution is lossy, and the mothership now
 * keeps the losing half: `vertical_subtype` distinguishes a PG from a resort
 * so iBOS can branch onboarding (beds and monthly tenants, not rooms and
 * nightly rates).
 *
 * The server validates this against SUBTYPES in verticals_canonical.py and
 * rejects anything it does not recognise, so sending the raw parameter is
 * safe — an unknown value is refused rather than stored.
 *
 * Deliberately NOT resolved here: we want the word the visitor clicked
 * ("pg"), not what it maps to.
 */
function subtypeFromUrl(): string | null {
  const raw = new URLSearchParams(window.location.search).get("vertical");
  if (!raw) return null;
  const key = raw.trim().toLowerCase().replace(/[-\s]/g, "_");
  // Only send it when it actually differs from the canonical slug; a visitor
  // arriving with ?vertical=hotel named the vertical itself, not a flavour.
  const canonical = resolveVertical(key);
  return canonical && key !== canonical ? key : null;
}

function resolveInitialVertical(draftVertical?: string): string {
  const fromUrl = resolveVertical(new URLSearchParams(window.location.search).get("vertical"));
  if (fromUrl && isSelectableVertical(fromUrl)) return fromUrl;

  if (draftVertical && draftVertical !== "general" && isSelectableVertical(draftVertical)) {
    return draftVertical;
  }

  const fromCookie = getVerticalIntent();
  if (fromCookie && isSelectableVertical(fromCookie)) return fromCookie;

  return "general";
}

// ---------------------------------------------------------------------------
// Business address type-ahead
//
// This used to call new google.maps.places.Autocomplete() when
// window.google.maps.places existed. Nothing ever loaded the Maps JS API —
// there is no <script> tag in public/index.html and no API key anywhere in this
// repo — so the guard returned on every render and the field silently offered
// no suggestions at all.
//
// Replaced with Photon: the OSM project's own type-ahead geocoder, keyless,
// CORS-open, and the same OpenStreetMap data the platform's maps_service
// (poc.axoix.io/api/app/services/external/maps_service.py) already standardises
// on. Nominatim is deliberately NOT used — its usage policy forbids
// autocomplete-style querying; Photon exists precisely for it.
// ---------------------------------------------------------------------------
const PHOTON_URL = "https://photon.komoot.io/api/";
const INDIA_BBOX = "68.1,6.5,97.4,35.7"; // minLon,minLat,maxLon,maxLat

// Photon feature types that describe an area rather than a street address —
// their `name` is the city/district itself and must not become an address line.
const AREA_TYPES = new Set(["city", "county", "state", "country", "district", "locality"]);

interface AddressSuggestion {
  label: string;       // what the dropdown row shows
  addressLine: string;
  city: string;
  state: string;
  pincode: string;
}

/** Free-text state name → the exact INDIAN_STATES entry the <select> holds. */
function matchIndianState(raw: string): string {
  const needle = (raw || "").trim().toLowerCase();
  if (!needle) return "";
  const exact = INDIAN_STATES.find((s) => s.toLowerCase() === needle);
  if (exact) return exact;
  // "NCT of Delhi", "Orissa (Odisha)" and similar upstream spellings.
  return (
    INDIAN_STATES.find(
      (s) => needle.includes(s.toLowerCase()) || s.toLowerCase().includes(needle)
    ) || ""
  );
}

function photonFeatureToSuggestion(feature: any): AddressSuggestion | null {
  const p = feature?.properties;
  if (!p || p.countrycode !== "IN") return null;

  const isArea = AREA_TYPES.has(p.type);
  const state = matchIndianState(p.state || "");
  const pincode = /^\d{6}$/.test(String(p.postcode || "")) ? String(p.postcode) : "";
  const city = (isArea ? p.name : p.city) || p.city || p.county || p.district || "";

  // `name` is the building or landmark ("Taj MG Road"), `street` the road it
  // sits on. Keeping both is what lets a hotel find itself by name; the dedupe
  // pass collapses them when OSM stores the same string in each.
  const streetLine = [p.housenumber, p.street].filter(Boolean).join(" ");
  const addressLine = isArea
    ? ""
    : [p.name, streetLine, p.district !== city ? p.district : null]
        .filter(Boolean)
        .filter((part: string, i: number, all: string[]) => all.indexOf(part) === i)
        .join(", ");

  const label = [addressLine, city, state, pincode].filter(Boolean).join(", ");
  if (!label) return null;
  return { label, addressLine, city, state, pincode };
}

async function fetchAddressSuggestions(
  query: string,
  signal: AbortSignal
): Promise<AddressSuggestion[]> {
  const res = await fetch(
    `${PHOTON_URL}?q=${encodeURIComponent(query)}&limit=10&lang=en&bbox=${INDIA_BBOX}`,
    { signal }
  );
  if (!res.ok) throw new Error(`photon ${res.status}`);
  const data = await res.json();

  const seen = new Set<string>();
  const out: AddressSuggestion[] = [];
  const features: any[] = Array.isArray(data?.features) ? data.features : [];
  for (const feature of features) {
    const suggestion = photonFeatureToSuggestion(feature);
    if (!suggestion || seen.has(suggestion.label)) continue;
    seen.add(suggestion.label);
    out.push(suggestion);
    if (out.length === 6) break;
  }
  return out;
}

/** India Post pincode directory — free, keyless, authoritative for city/state. */
async function lookupPincode(pin: string): Promise<{ city: string; state: string } | null> {
  try {
    const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
    if (!res.ok) return null;
    const data = await res.json();
    const po = data?.[0]?.PostOffice?.[0];
    if (!po) return null;
    const city = po.District || po.Block || po.Name || "";
    const state = matchIndianState(po.State || "");
    return city || state ? { city, state } : null;
  } catch {
    return null;
  }
}

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  // Vertical price and per-tier vertical limits come from the Mothership, not
  // from a constant in api/pricing.tsx. The old getVerticalPrice() derived
  // Rs 10,000 x (1 - 60%) = Rs 4,000 and matched the API only by coincidence.
  const {
    verticalMonthlyPrice,
    tierVerticalLimits: TIER_VERTICAL_LIMITS,
  } = useVerticalPricing();

  // Live tier prices — the same source /pricing renders from. The dropdown used
  // to carry its own copy of these numbers and every one of them had drifted
  // (Officer Monthly ₹4,999 shown against ₹1,500 charged). The Mothership
  // prices the order from pricing_tiers regardless of what the client sends, so
  // a stale label here is a quote the customer never actually pays.
  const { pricingTiers } = usePricingData();
  const [verticalSearch, setVerticalSearch] = useState("");

  const paidPlanOptions = useMemo(() => {
    const priceOf = (tierId: string, cycle: string): number | null => {
      const tier = pricingTiers.find((x) => x.id === tierId);
      if (!tier) return null;
      const map: Record<string, number | null | undefined> = {
        monthly: tier.monthlyPrice,
        quarterly: tier.quarterlyPrice,
        sixMonth: tier.sixMonthPrice,
        yearly: tier.yearlyPrice,
      };
      const v = map[cycle];
      return typeof v === "number" ? v : null;
    };

    return PAID_PLAN_SHAPES.map((shape) => {
      const price = priceOf(shape.tier, shape.cycle);
      const tierNames: Record<string, string> = {
        officer: "Officer",
        commander: "Commander",
        titan: "Titan",
      };
      const tierName = tierNames[shape.tier] || shape.tier;
      // No price yet (still loading, or the API is down) => name the plan
      // without inventing a figure. A wrong number is worse than none.
      const label =
        price == null
          ? `${tierName} ${shape.cycleLabel}`
          : `${tierName} ${shape.cycleLabel} - ₹${price.toLocaleString("en-IN")}${shape.suffix}`;
      return { value: shape.value, label };
    });
  }, [pricingTiers]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [subdomainError, setSubdomainError] = useState<string>("");
  const [subdomainIsPending, setSubdomainIsPending] = useState(false);
  const [pendingMaskedEmail, setPendingMaskedEmail] = useState("");
  const [pendingResumeUrl, setPendingResumeUrl] = useState("");
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeNotice, setResumeNotice] = useState("");
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [apiAvailable, setApiAvailable] = useState<boolean | null>(null);
  const [referrerName, setReferrerName] = useState<string>("");
  const [subdomainSuggestions, setSubdomainSuggestions] = useState<string[]>([]);
  const [arrivedFromPG, setArrivedFromPG] = useState(false);
  const subdomainManuallyEdited = useRef(false);
  // The subdomain the LATEST check was fired for. Every keystroke fires its own
  // request and they do not come back in order: typing "dytyvuyg" launches six,
  // and the reply for "dyt" ("must be at least 4 characters") can land after the
  // reply for the full name. Without this guard the late, wrong answer wins and
  // a perfectly valid 8-character subdomain shows a 4-character error. Reported
  // live on 2026-09-01. A response is applied only if it is still the one we
  // want.
  const subdomainRequestFor = useRef("");

  // Business address type-ahead
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);
  const [addressActive, setAddressActive] = useState(-1);
  const addressBoxRef = useRef<HTMLDivElement | null>(null);
  // Holds the text a picked suggestion wrote into the field, so the debounce
  // below can tell "the user typed this" from "we just filled this in".
  const addressPickedValue = useRef<string | null>(null);
  const pincodeLookedUp = useRef("");

  // Email OTP verification state
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationToken, setEmailVerificationToken] = useState("");
  const [emailVerificationExpiresAt, setEmailVerificationExpiresAt] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Whether the paid plans in the select below can actually be bought — the
  // same answer the pricing page and the checkout render from, so the three
  // can no longer contradict each other. See useSaleOpen().
  const {
    saleOpen: paidPlansEnabled,
    testMode: paymentTestMode,
    loading: saleLoading,
    unlocked: saleUnlocked,
  } = useSaleOpen();
  // A paid plan arrived from ?tier= or a draft but cannot be bought — we tell
  // the visitor instead of silently swapping it for Explorer behind their back.
  const [planDowngradeNotice, setPlanDowngradeNotice] = useState<string | null>(null);

  const draft = loadSignupDraft();
  const initialVertical = resolveInitialVertical(draft?.primary_vertical);

  // Remembered from the first render so the "we picked this for you" note can
  // disappear the moment the visitor overrides it with their own choice.
  const prefilledVertical = useRef<string | null>(null);
  if (prefilledVertical.current === null) {
    prefilledVertical.current = initialVertical === "general" ? "" : initialVertical;
  }

  const [formData, setFormData] = useState<SignupFormData>({
    company_name: draft?.company_name || "",
    subdomain: draft?.subdomain || "",
    admin_email: draft?.admin_email || "",
    admin_password: "", // never persisted — always starts blank
    admin_name: draft?.admin_name || "",
    phone: draft?.phone || "",
    plan: draft?.plan || "explorer",
    referral_code: draft?.referral_code || "",
    primary_vertical: initialVertical,
    // A stale draft can carry the new primary in the addon list — it would then
    // be billed as an extra vertical the tenant already has.
    addon_verticals: (draft?.addon_verticals || []).filter((v) => v !== initialVertical),
    state: draft?.state || "",
    city: draft?.city || "",
    pincode: draft?.pincode || "",
    address_line: draft?.address_line || "",
    address_line_2: draft?.address_line_2 || "",
    gstin: draft?.gstin || "",
  });

  // Available industry options for multi-selection (excluding primary & general)
  const availableVerticals = useMemo(() => {
    return INDUSTRY_OPTIONS.filter(
      (opt) => opt.value !== formData.primary_vertical && opt.value !== "general"
    );
  }, [formData.primary_vertical]);

  // Filtered verticals based on search query
  const filteredVerticals = useMemo(() => {
    if (!verticalSearch.trim()) return availableVerticals;
    const q = verticalSearch.toLowerCase().trim();
    return availableVerticals.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
    );
  }, [availableVerticals, verticalSearch]);

  // Rehydrate verification state from the draft — but ONLY if the token hasn't
  // expired yet (~30min server-side). A stale draft rehydrated as "verified"
  // would let the user resubmit the same already-expired token forever.
  useEffect(() => {
    if (
      draft?.emailVerified &&
      draft.emailVerificationToken &&
      draft.admin_email &&
      draft.emailVerificationExpiresAt > Date.now()
    ) {
      setEmailVerified(true);
      setEmailVerificationToken(draft.emailVerificationToken);
      setEmailVerificationExpiresAt(draft.emailVerificationExpiresAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist every field except password on every change
  useEffect(() => {
    saveSignupDraft({
      company_name: formData.company_name,
      subdomain: formData.subdomain,
      admin_email: formData.admin_email,
      admin_name: formData.admin_name,
      phone: formData.phone,
      plan: formData.plan,
      referral_code: formData.referral_code,
      primary_vertical: formData.primary_vertical,
      addon_verticals: formData.addon_verticals,
      state: formData.state,
      city: formData.city,
      pincode: formData.pincode,
      address_line: formData.address_line,
      address_line_2: formData.address_line_2,
      gstin: formData.gstin,
      emailVerified,
      emailVerificationToken,
      emailVerificationExpiresAt,
    });
  }, [formData, emailVerified, emailVerificationToken, emailVerificationExpiresAt]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // A paid plan can arrive from ?tier=officer (the pricing page CTA) or from a
  // saved draft. Once the storefront state resolves, fall back to Explorer if
  // that plan isn't buyable — otherwise the form would submit a plan the
  // select renders as disabled and the server would refuse it anyway.
  //
  // The fallback is announced. Previously it happened silently, so a visitor
  // who clicked "Get Started" on Officer landed here, saw Explorer selected,
  // and was never told why.
  // A paid plan can arrive from ?tier=officer (the pricing page CTA) or from a
  // saved draft. Once the storefront state resolves, fall back to Explorer if
  // that plan isn't buyable — otherwise the form would submit a plan the
  // select renders as disabled and the server would refuse it anyway.
  //
  // The fallback is announced. Previously it happened silently, so a visitor
  // who clicked "Get Started" on Officer landed here, saw Explorer selected,
  // and was never told why.
  useEffect(() => {
    if (saleLoading || paidPlansEnabled) return;
    // Titan used to be exempt here because it was never a real submittable
    // plan — the option was disabled in the dropdown and pointed at
    // /contact. Now that Titan is self-serve like Officer and Commander, it
    // must fall back to Explorer under the same closed-storefront rule as
    // every other paid plan, or a titan_* value could sit in state with the
    // paid dropdown disabled and no visible way to change it.
    if (formData.plan === "explorer") return;
    setFormData((prev) => ({ ...prev, plan: "explorer", addon_verticals: [] }));
    setPlanDowngradeNotice(
      "Paid plans aren't open for self-signup yet, so we've started you on Explorer (free forever). " +
        "You can upgrade from inside your dashboard the moment they open."
    );
  }, [paidPlansEnabled, saleLoading, formData.plan]);

  // Check API health and URL params on mount
  useEffect(() => {
    checkApiHealth().then(setApiAvailable);

    // Check for URL params
    const urlParams = new URLSearchParams(window.location.search);

    // Get tier from URL (e.g., ?tier=officer)
    const tierParam = urlParams.get("tier");
    const billingParam = urlParams.get("billing") as "monthly" | "quarterly" | "six_month" | "yearly" | null;

    // Map tier to plan value including billing cycle
    if (tierParam) {
      let planValue = tierParam; // explorer, officer, commander, titan

      // Append billing cycle suffix if provided and tier is not explorer.
      // Titan used to be excluded here, which meant ?tier=titan&billing=yearly
      // could never resolve to "titan_yearly" — silently discarding the cycle
      // on the one tier a customer was most likely to want billed annually.
      if (billingParam && tierParam !== "explorer") {
        if (billingParam === "quarterly") {
          planValue = `${tierParam}_quarterly`;
        } else if (billingParam === "six_month") {
          planValue = `${tierParam}_six_month`;
        } else if (billingParam === "yearly") {
          planValue = `${tierParam}_yearly`;
        }
        // monthly is the default, no suffix needed
      }

      setFormData((prev) => ({ ...prev, plan: planValue }));
    }

    // Check for referral code in URL first, then fallback to cookie
    const refCode = urlParams.get("ref") || getReferralCode();
    if (refCode) {
      setFormData((prev) => ({ ...prev, referral_code: refCode }));
      validateReferralCode(refCode).then((result) => {
        if (result.valid && result.partner_name) {
          setReferrerName(result.partner_name);
        }
      });
    }

    // The industry vertical is resolved in the useState initializer above
    // (?vertical= → draft → industry cookie), not here — doing it in an effect
    // rendered "General Business" for one frame first.

    // ?from=pg — arrived from /pg-hostel-management-software. PG is a property
    // type inside the hotel vertical, so there is no PG option in this dropdown
    // (and can't be until VERTICAL_MODULES gains a `pg` key). Explain that here
    // rather than let the operator think we don't support PG.
    if (urlParams.get("from") === "pg") {
      setArrivedFromPG(true);
    }
  }, []);

  // Debounced address suggestions. See the Photon note above the helpers.
  useEffect(() => {
    // Writing the chosen address back into the field must not immediately
    // re-query with the text we just put there. Compared by value rather than a
    // one-shot flag: picking a suggestion whose text already matches the field
    // fires no effect at all, and a stale flag would then eat the next keystroke.
    if (addressPickedValue.current === formData.address_line) {
      addressPickedValue.current = null;
      return;
    }

    const query = formData.address_line.trim();
    if (query.length < 3) {
      setAddressSuggestions([]);
      setAddressOpen(false);
      setAddressLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setAddressLoading(true);

    const timer = setTimeout(() => {
      fetchAddressSuggestions(query, controller.signal)
        .then((results) => {
          if (cancelled) return;
          setAddressSuggestions(results);
          setAddressActive(-1);
          setAddressOpen(results.length > 0);
        })
        .catch(() => {
          // Offline, rate-limited, or the public instance is down. City, State
          // and Pincode are all still typeable by hand, so this stays a silent
          // convenience rather than a blocking error.
          if (!cancelled) setAddressSuggestions([]);
        })
        .finally(() => {
          if (!cancelled) setAddressLoading(false);
        });
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [formData.address_line]);

  // Close the suggestion list when the click lands outside it.
  useEffect(() => {
    if (!addressOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (addressBoxRef.current && !addressBoxRef.current.contains(e.target as Node)) {
        setAddressOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [addressOpen]);

  // A complete pincode fills City and State from the India Post directory.
  //
  // Keyed on the pincode alone. Depending on city/state as well looked tidier —
  // it skipped the request once both were filled — but it also re-ran the effect
  // while the lookup was in flight, and the cleanup then cancelled a perfectly
  // good result: type a pincode, start typing the city, and the state never
  // arrived. The "don't overwrite what was typed" rule is enforced inside the
  // updater instead, against the freshest state, which is where it belongs.
  useEffect(() => {
    const pin = formData.pincode;
    if (!/^\d{6}$/.test(pin) || pincodeLookedUp.current === pin) return;

    pincodeLookedUp.current = pin;
    let cancelled = false;
    lookupPincode(pin).then((result) => {
      if (cancelled || !result) return;
      setFormData((prev) => ({
        ...prev,
        ...(result.city && !prev.city.trim() && { city: result.city }),
        ...(result.state && !prev.state && { state: result.state }),
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [formData.pincode]);

  const applyAddressSuggestion = (suggestion: AddressSuggestion) => {
    const line = suggestion.addressLine || suggestion.label;
    addressPickedValue.current = line;
    setFormData((prev) => ({
      ...prev,
      address_line: line,
      ...(suggestion.city && { city: suggestion.city }),
      ...(suggestion.state && { state: suggestion.state }),
      ...(suggestion.pincode && { pincode: suggestion.pincode }),
    }));
    setAddressSuggestions([]);
    setAddressOpen(false);
    setAddressActive(-1);
  };

  const handleAddressKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!addressOpen || addressSuggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAddressActive((i) => (i + 1) % addressSuggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAddressActive((i) => (i <= 0 ? addressSuggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      // Enter must still submit the form when no row is highlighted.
      if (addressActive >= 0) {
        e.preventDefault();
        applyAddressSuggestion(addressSuggestions[addressActive]);
      }
    } else if (e.key === "Escape") {
      setAddressOpen(false);
    }
  };

  // Check subdomain availability with debounce
  const checkSubdomain = useCallback(async (subdomain: string, companyName?: string) => {
    // Claim this keystroke as the current one BEFORE any await. Everything below
    // re-checks the claim before touching state, so an earlier request that
    // finishes late cannot overwrite a later, correct answer.
    subdomainRequestFor.current = subdomain;
    const isStale = () => subdomainRequestFor.current !== subdomain;

    // First validate format locally
    const validation = validateSubdomain(subdomain);
    if (!validation.valid) {
      setSubdomainError(validation.message || "");
      setSubdomainAvailable(null);
      setSubdomainSuggestions([]);
      return;
    }

    setSubdomainError("");
    setSubdomainSuggestions([]);
    setCheckingSubdomain(true);

    try {
      // Check with API if available
      if (apiAvailable) {
        const result = await checkSubdomainApi(subdomain, companyName);
        if (isStale()) return;
        setSubdomainAvailable(result.available);
        setSubdomainIsPending(Boolean(result.is_pending));
        setPendingMaskedEmail(result.masked_email || "");
        setPendingResumeUrl(result.resume_url || "");
        if (!result.available) {
          setSubdomainError(result.message || "This subdomain is already taken");
          setSubdomainSuggestions(result.suggestions || []);
        } else {
          setSubdomainError("");
        }
      } else {
        // API not available - just validate locally
        if (isStale()) return;
        setSubdomainAvailable(true);
        setSubdomainIsPending(false);
        setPendingResumeUrl("");
      }
    } catch {
      if (isStale()) return;
      setSubdomainAvailable(null);
      setSubdomainIsPending(false);
      setPendingResumeUrl("");
    } finally {
      // Only the newest request may clear the spinner, or a late straggler
      // turns it off while the current check is still running.
      if (!isStale()) setCheckingSubdomain(false);
    }
  }, [apiAvailable]);

  const handleRequestResume = async () => {
    if (pendingResumeUrl) {
      window.location.href = pendingResumeUrl;
      return;
    }
    if (!formData.subdomain) return;
    setResumeLoading(true);
    setResumeNotice("");
    try {
      const payload: RequestResumeRequest = {
        subdomain: formData.subdomain,
      };
      if (emailVerified && formData.admin_email && emailVerificationToken) {
        payload.email = formData.admin_email;
        payload.email_verification_token = emailVerificationToken;
      }
      const res = await requestResumeLink(payload);
      if (res.resumed && res.resume_url) {
        window.location.href = res.resume_url;
        return;
      }
      setResumeNotice(res.message || `A setup link has been sent to ${res.masked_email || 'your email'}. Check your inbox!`);
    } catch (err: any) {
      setResumeNotice(err.message || "Failed to request setup link. Please contact support.");
    } finally {
      setResumeLoading(false);
    }
  };

  // Prefill and check subdomain if passed in query param (?subdomain=...)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subParam = urlParams.get("subdomain");
    if (subParam && !subdomainManuallyEdited.current) {
      const sanitized = subParam.toLowerCase().replace(/[^a-z0-9-]/g, "");
      setFormData((prev) => ({ ...prev, subdomain: sanitized }));
      subdomainManuallyEdited.current = true;
      checkSubdomain(sanitized);
    }
  }, [checkSubdomain]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === "admin_email") {
      // Editing the email after verification invalidates it — must re-verify
      setEmailVerified(false);
      setEmailVerificationToken("");
      setEmailVerificationExpiresAt(0);
      setOtpSent(false);
      setOtpValue("");
      setOtpError("");
    }

    if (name === "primary_vertical") {
      // The addon chips already hide whatever is selected as primary, but the
      // value stayed in addon_verticals — so choosing Hotel as an extra industry
      // and then promoting it to primary billed the same vertical twice.
      setFormData(prev => ({
        ...prev,
        primary_vertical: value,
        addon_verticals: prev.addon_verticals.filter(v => v !== value),
      }));
    }

    if (name === "company_name") {
      // Auto-generate subdomain from company name (unless user manually edited it)
      if (!subdomainManuallyEdited.current) {
        const autoSubdomain = companyToSubdomain(value);
        setFormData(prev => ({ ...prev, [name]: value, subdomain: autoSubdomain }));
        if (autoSubdomain.length >= 3) {
          checkSubdomain(autoSubdomain, value);
        } else {
          setSubdomainAvailable(null);
          setSubdomainError("");
          setSubdomainSuggestions([]);
        }
      }
    }

    if (name === "subdomain") {
      subdomainManuallyEdited.current = true;
      const cleanSubdomain = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({ ...prev, subdomain: cleanSubdomain }));
      // >= 4, not >= 3. The API's own minimum is 4
      // (tenants.py check_subdomain_availability), so a 3-character check can
      // only ever come back "must be at least 4 characters" — it fires a request
      // whose sole possible outcome is an error message about a name the user is
      // still halfway through typing.
      if (cleanSubdomain.length >= 4) {
        checkSubdomain(cleanSubdomain, formData.company_name);
      } else {
        setSubdomainAvailable(null);
        setSubdomainError("");
        setSubdomainSuggestions([]);
      }
      // Reset manual flag if user clears the subdomain
      if (!value) subdomainManuallyEdited.current = false;
    }
  };

  const isValidEmailFormat = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSendOtp = async () => {
    if (!isValidEmailFormat(formData.admin_email)) return;
    setOtpError("");
    setOtpSending(true);
    try {
      const result = await sendSignupOtp(formData.admin_email);
      setOtpSent(true);
      setResendCooldown(result.cooldown_seconds || 45);
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : "Failed to send code");
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) return;
    setOtpError("");
    setOtpVerifying(true);
    try {
      const result = await verifySignupOtp(formData.admin_email, otpValue);
      if (result.email_verification_token) {
        setEmailVerified(true);
        setEmailVerificationToken(result.email_verification_token);
        // Mirrors the backend's 30-minute token expiry (security.py's
        // create_email_verification_token) so a stale draft never rehydrates
        // as falsely "verified."
        setEmailVerificationExpiresAt(Date.now() + 30 * 60 * 1000);
      }
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : "Invalid or expired code");
    } finally {
      setOtpVerifying(false);
    }
  };

  // Handle clicking a subdomain suggestion
  const handleSuggestionClick = (suggestion: string) => {
    subdomainManuallyEdited.current = true;
    setFormData(prev => ({ ...prev, subdomain: suggestion }));
    setSubdomainSuggestions([]);
    checkSubdomain(suggestion, formData.company_name);
  };

  // Determine billing cycle based on plan.
  //
  // "titan" used to map to "five_year" here, because Titan sold only on
  // 5/10-year terms and had no five_year_price mapped through this cycle
  // string anyway (tenants.py accepts a plain "titan" plan on monthly/
  // quarterly/six_month/yearly the same as any other tier since 2026-09-08).
  // Titan's fiveYearPrice/tenYearPrice are NULL now — sending "five_year"
  // would ask the server for a price that no longer exists and get refused.
  const getBillingCycle = (plan: string): "monthly" | "quarterly" | "six_month" | "yearly" => {
    if (plan.endsWith("_quarterly")) return "quarterly";
    if (plan.endsWith("_six_month")) return "six_month";
    if (plan.endsWith("_yearly")) return "yearly";
    return "monthly";
  };

  // Get base plan ID (without billing cycle suffix)
  const getBasePlanId = (plan: string): string => {
    return plan.replace(/_quarterly$/, "").replace(/_six_month$/, "").replace(/_yearly$/, "");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate subdomain
    const validation = validateSubdomain(formData.subdomain);
    if (!validation.valid) {
      setError(validation.message || "Invalid subdomain");
      setLoading(false);
      return;
    }

    if (!emailVerified || !emailVerificationToken || emailVerificationExpiresAt < Date.now()) {
      // Covers both "never verified" and "verified >30min ago, sat on this tab" —
      // the in-memory state doesn't self-expire, only rehydration re-checks it.
      setEmailVerified(false);
      setEmailVerificationToken("");
      setError(
        emailVerificationExpiresAt && emailVerificationExpiresAt < Date.now()
          ? "Your email verification expired. Please verify again."
          : "Please verify your email address before continuing."
      );
      setLoading(false);
      return;
    }

    // Validate phone (mandatory)
    const cleanPhone = formData.phone.trim().replace(/\D/g, "");
    if (!formData.phone.trim()) {
      setError("Please enter your phone number");
      setLoading(false);
      return;
    }
    if (cleanPhone.length < 10) {
      setError("Please enter a valid phone number (at least 10 digits)");
      setLoading(false);
      return;
    }

    // Validate address (Address Line 1 is mandatory)
    if (!formData.address_line.trim()) {
      setError("Please enter Address Line 1");
      setLoading(false);
      return;
    }
    if (!formData.state) {
      setError("Please select your state");
      setLoading(false);
      return;
    }
    if (!formData.city.trim()) {
      setError("Please enter your city");
      setLoading(false);
      return;
    }

    // ALL plans go through checkout (T&C acceptance + signature required)
    // Explorer skips Razorpay, paid plans go through payment after T&C
    const fullAddressLine = formData.address_line_2?.trim()
      ? `${formData.address_line.trim()}, ${formData.address_line_2.trim()}`
      : formData.address_line.trim();

    setLoading(false);
    // Details step done — the lead exists from here, whether or not checkout completes.
    track("generate_lead", { source: "signup_form", plan: formData.plan });
    navigate("/checkout", {
      state: {
        company_name: formData.company_name,
        subdomain: formData.subdomain,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
        admin_name: formData.admin_name,
        phone: formData.phone,
        plan: formData.plan === "explorer" ? "explorer" : getBasePlanId(formData.plan),
        referral_code: formData.referral_code,
        billing_cycle: formData.plan === "explorer" ? "monthly" : getBillingCycle(formData.plan),
        primary_vertical: formData.primary_vertical,
        // Sent only when the visitor arrived on a subtype URL (?vertical=pg).
        // The mothership stores it as tenants.settings.vertical_subtype and
        // hands it to iBOS at login; null just means "no flavour named".
        vertical_subtype: subtypeFromUrl(),
        addon_verticals: formData.addon_verticals,
        email_verification_token: emailVerificationToken,
        // Address & GST
        state: formData.state || null,
        city: formData.city || null,
        pincode: formData.pincode || null,
        address_line: fullAddressLine || null,
        address_line_1: formData.address_line.trim() || null,
        address_line_2: formData.address_line_2?.trim() || null,
        gstin: formData.gstin || null,
      },
    });
  };

  // Shown only while the visitor is still sitting on the industry we guessed —
  // the moment they change the dropdown, the note is no longer true.
  const prefilledVerticalLabel =
    prefilledVertical.current && formData.primary_vertical === prefilledVertical.current
      ? INDUSTRY_OPTIONS.find((opt) => opt.value === prefilledVertical.current)?.label || ""
      : "";

  return (
    <Fragment>
      <SEO title="Sign Up" description="Create your AXOIX account — choose your industry vertical, pick a plan, and get your dedicated business dashboard at yourcompany.axoix.io." path="/signup" />
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main
            style={{
              backgroundImage: `url(${pricingBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center top",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "fixed",
            }}
          >
            <PageTitle pageTitle="Join the AXOIX Crew" pagesub="Sign Up" />
            <section className="contact pt-100 pb-120">
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-lg-10">
                    <div
                      className="pricing-item xb-border bg_img xb-signup-form-container"
                      style={{ backgroundImage: `url(${cardBg})`, padding: '50px 40px' }}
                    >
                    <style>{`
                      .xb-signup-form-container input[type="text"],
                      .xb-signup-form-container input[type="password"],
                      .xb-signup-form-container input[type="email"],
                      .xb-signup-form-container input[type="tel"],
                      .xb-signup-form-container select {
                        width: 100% !important;
                        height: 46px !important;
                        line-height: 46px !important;
                        padding: 0 16px !important;
                        background: rgba(10, 25, 47, 0.9) !important;
                        border: 1px solid rgba(0, 255, 151, 0.25) !important;
                        border-radius: 8px !important;
                        color: #ffffff !important;
                        font-size: 14px !important;
                        font-weight: 400 !important;
                        outline: none !important;
                        box-shadow: none !important;
                        transition: all 0.2s ease !important;
                      }

                      .xb-signup-form-container input:focus,
                      .xb-signup-form-container select:focus {
                        border-color: #00ff97 !important;
                        background: rgba(15, 35, 60, 0.98) !important;
                        box-shadow: 0 0 12px rgba(0, 255, 151, 0.2) !important;
                      }

                      .xb-signup-form-container input::placeholder {
                        color: rgba(255, 255, 255, 0.38) !important;
                        font-size: 13px !important;
                      }

                      .xb-signup-form-container input:-webkit-autofill,
                      .xb-signup-form-container input:-webkit-autofill:hover,
                      .xb-signup-form-container input:-webkit-autofill:focus,
                      .xb-signup-form-container input:-webkit-autofill:active {
                        -webkit-box-shadow: 0 0 0 50px #0b1a30 inset !important;
                        -webkit-text-fill-color: #ffffff !important;
                        border-color: rgba(0, 255, 151, 0.4) !important;
                      }

                      .xb-signup-form-container select {
                        cursor: pointer !important;
                        appearance: none !important;
                        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2300ff97' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E") !important;
                        background-repeat: no-repeat !important;
                        background-position: right 14px center !important;
                        padding-right: 36px !important;
                      }

                      .xb-signup-form-container select option {
                        background: #0a192f !important;
                        color: #ffffff !important;
                        padding: 10px 14px !important;
                      }

                      .xb-signup-form-container label {
                        font-size: 13px !important;
                        font-weight: 500 !important;
                        color: rgba(255, 255, 255, 0.92) !important;
                        margin-bottom: 8px !important;
                        display: block !important;
                        letter-spacing: 0.01em !important;
                      }

                      .xb-signup-form-container .custom-vertical-scroll {
                        overscroll-behavior: contain !important;
                        -webkit-overflow-scrolling: touch !important;
                      }
                      .xb-signup-form-container .custom-vertical-scroll::-webkit-scrollbar {
                        width: 6px;
                      }
                      .xb-signup-form-container .custom-vertical-scroll::-webkit-scrollbar-track {
                        background: rgba(255, 255, 255, 0.03);
                        border-radius: 4px;
                      }
                      .xb-signup-form-container .custom-vertical-scroll::-webkit-scrollbar-thumb {
                        background: rgba(0, 255, 151, 0.35);
                        border-radius: 4px;
                      }
                      .xb-signup-form-container .custom-vertical-scroll::-webkit-scrollbar-thumb:hover {
                        background: rgba(0, 255, 151, 0.6);
                      }
                    `}</style>
                    <div className="form-heading text-center mb-30">
                      <h3 className="title">Create Your AXOIX Dashboard</h3>
                      <p className="sub-title clr-white">
                        Get your AI-powered business command center in minutes
                      </p>
                    </div>

                    {error && (
                      <div className="services-outcome-wrap mb-30" style={{ borderColor: "#ff4444" }}>
                        <p style={{ color: "#ff4444", margin: 0 }}>
                          <i className="fas fa-exclamation-circle"></i> {error}
                        </p>
                      </div>
                    )}

                    {referrerName && (
                      <div className="services-outcome-wrap mb-30" style={{ borderColor: "var(--color-primary)" }}>
                        <p style={{ color: "var(--color-primary)", margin: 0 }}>
                          <i className="fas fa-user-check"></i> Referred by: {referrerName}
                        </p>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="xb-contact-form-wrap">
                      <div className="row">
                        {/* Company Name */}
                        <div className="col-lg-6">
                          <div className="xb-form-input mb-25">
                            <label>Company Name <span style={{ color: "#00ff97" }}>*</span></label>
                            <input
                              type="text"
                              name="company_name"
                              placeholder="Your company name"
                              value={formData.company_name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>

                        {/* Subdomain */}
                        <div className="col-lg-6">
                          <div className="xb-form-input mb-25">
                            <label>Choose Your Subdomain <span style={{ color: "#00ff97" }}>*</span></label>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <input
                                type="text"
                                name="subdomain"
                                placeholder="yourcompany"
                                value={formData.subdomain}
                                onChange={handleChange}
                                required
                                minLength={3}
                                style={{ flex: 1 }}
                              />
                              <span
                                style={{
                                  marginLeft: "8px",
                                  padding: "0 14px",
                                  height: "46px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  background: "rgba(10, 25, 47, 0.9)",
                                  border: "1px solid rgba(0, 255, 151, 0.25)",
                                  borderRadius: "8px",
                                  color: "#00ff97",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                .axoix.io
                              </span>
                            </div>
                            {formData.subdomain && (
                              <div style={{ marginTop: "6px" }}>
                                {checkingSubdomain ? (
                                  <span className="clr-white" style={{ fontSize: "13px" }}>
                                    <i className="fas fa-spinner fa-spin" style={{ marginRight: "6px" }}></i>
                                    Checking availability...
                                  </span>
                                ) : subdomainError ? (
                                  <div>
                                    <span style={{ color: "#ff4444", fontSize: "13px" }}>✗ {subdomainError}</span>
                                    {subdomainIsPending && (
                                      <div style={{
                                        marginTop: "10px",
                                        padding: "12px 16px",
                                        background: "linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(0, 255, 151, 0.1) 100%)",
                                        border: "1px solid rgba(0, 255, 151, 0.4)",
                                        borderRadius: "10px",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)"
                                      }}>
                                        <p style={{ margin: "0 0 8px 0", fontSize: "13px", color: "#e2e8f0", lineHeight: 1.5 }}>
                                          <strong>{formData.subdomain}.axoix.io</strong> is already registered {pendingMaskedEmail ? `(${pendingMaskedEmail})` : ""} and waiting for payment or activation.
                                        </p>
                                        <button
                                          type="button"
                                          onClick={handleRequestResume}
                                          disabled={resumeLoading}
                                          style={{
                                            background: "linear-gradient(90deg, #06b6d4, #00ff97)",
                                            border: "none",
                                            color: "#00020f",
                                            borderRadius: "6px",
                                            padding: "8px 18px",
                                            fontSize: "13px",
                                            fontWeight: 700,
                                            cursor: "pointer",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            boxShadow: "0 2px 8px rgba(0, 255, 151, 0.3)",
                                          }}
                                        >
                                          {resumeLoading ? (
                                            <>
                                              <i className="fas fa-spinner fa-spin"></i> Loading Setup...
                                            </>
                                          ) : (
                                            <>
                                              <i className="fas fa-arrow-right"></i> Complete Setup & Pay for {formData.subdomain} &rarr;
                                            </>
                                          )}
                                        </button>
                                        {resumeNotice && (
                                          <p style={{ margin: "8px 0 0 0", fontSize: "12px", color: "#00ff97", fontWeight: 500 }}>
                                            {resumeNotice}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ) : subdomainAvailable === true ? (
                                  <span style={{ color: "#00ff97", fontSize: "13px" }}>✓ Available</span>
                                ) : subdomainAvailable === false ? (
                                  <span style={{ color: "#ff4444", fontSize: "13px" }}>✗ Already taken</span>
                                ) : null}
                              </div>
                            )}
                            {subdomainSuggestions.length > 0 && (
                              <div style={{ marginTop: "6px" }}>
                                <span className="clr-white" style={{ fontSize: "12px", opacity: 0.7 }}>Try: </span>
                                {subdomainSuggestions.map((s) => (
                                  <button
                                    key={s}
                                    type="button"
                                    onClick={() => handleSuggestionClick(s)}
                                    style={{
                                      background: "rgba(255,255,255,0.1)",
                                      border: "1px solid rgba(255,255,255,0.25)",
                                      color: "#00ff97",
                                      borderRadius: "14px",
                                      padding: "2px 10px",
                                      margin: "2px 4px",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                      transition: "all 0.2s",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = "rgba(0,255,151,0.15)";
                                      e.currentTarget.style.borderColor = "#00ff97";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
                                    }}
                                  >
                                    {s}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Full Name */}
                        <div className="col-lg-6">
                          <div className="xb-form-input mb-25">
                            <label>Your Full Name <span style={{ color: "#00ff97" }}>*</span></label>
                            <input
                              type="text"
                              name="admin_name"
                              placeholder="John Doe"
                              value={formData.admin_name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>

                        {/* Email Address & Verification */}
                        <div className="col-lg-6">
                          <div className="xb-form-input mb-25">
                            <label>Email Address <span style={{ color: "#00ff97" }}>*</span></label>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <input
                                type="email"
                                name="admin_email"
                                placeholder="john@company.com"
                                value={formData.admin_email}
                                onChange={handleChange}
                                required
                                style={{ flex: 1 }}
                              />
                              {isValidEmailFormat(formData.admin_email) && !emailVerified && (
                                <button
                                  type="button"
                                  onClick={handleSendOtp}
                                  disabled={otpSending || resendCooldown > 0}
                                  style={{
                                    marginLeft: "8px",
                                    height: "46px",
                                    padding: "0 16px",
                                    background: "rgba(0, 255, 151, 0.12)",
                                    border: "1px solid var(--color-primary)",
                                    borderRadius: "8px",
                                    color: "var(--color-primary)",
                                    cursor: otpSending || resendCooldown > 0 ? "not-allowed" : "pointer",
                                    whiteSpace: "nowrap",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  {otpSending
                                    ? "Sending..."
                                    : resendCooldown > 0
                                    ? `Resend (${resendCooldown}s)`
                                    : otpSent
                                    ? "Resend Code"
                                    : "Verify Email"}
                                </button>
                              )}
                              {emailVerified && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    height: "46px",
                                    padding: "0 14px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    background: "rgba(0, 255, 151, 0.12)",
                                    border: "1px solid rgba(0, 255, 151, 0.35)",
                                    borderRadius: "8px",
                                    color: "#00ff97",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <i className="fas fa-check-circle"></i> Verified
                                </span>
                              )}
                            </div>

                            {otpSent && !emailVerified && (
                              <div style={{ marginTop: "10px", display: "flex", gap: "10px", alignItems: "center" }}>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={6}
                                  placeholder="6-digit code"
                                  value={otpValue}
                                  onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                  style={{ width: "140px", letterSpacing: "4px", textAlign: "center" }}
                                />
                                <button
                                  type="button"
                                  onClick={handleVerifyOtp}
                                  disabled={otpVerifying || otpValue.length !== 6}
                                  className="thm-btn agency-btn"
                                  style={{ height: "46px", padding: "0 20px", display: "inline-flex", alignItems: "center" }}
                                >
                                  <span className="text">{otpVerifying ? "Verifying..." : "Confirm OTP"}</span>
                                </button>
                              </div>
                            )}
                            {otpError && (
                              <p className="mt-5 mb-0" style={{ color: "#ff4444", fontSize: "12px" }}>
                                {otpError}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Password */}
                        <div className="col-lg-6">
                          <div className="xb-form-input mb-25">
                            <label>Create Password <span style={{ color: "#00ff97" }}>*</span></label>
                            <div style={{ position: "relative" }}>
                              <input
                                type={showPassword ? "text" : "password"}
                                name="admin_password"
                                placeholder="Min 8 characters"
                                value={formData.admin_password}
                                onChange={handleChange}
                                required
                                minLength={8}
                                style={{ paddingRight: "45px" }}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                aria-pressed={showPassword}
                                style={{
                                  position: "absolute",
                                  right: "14px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  background: "none",
                                  border: "none",
                                  color: "rgba(255, 255, 255, 0.6)",
                                  cursor: "pointer",
                                  padding: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "24px",
                                  height: "24px",
                                }}
                              >
                                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Phone Number */}
                        <div className="col-lg-6">
                          <div className="xb-form-input mb-25">
                            <label>Phone Number <span style={{ color: "#00ff97" }}>*</span></label>
                            <input
                              type="tel"
                              name="phone"
                              placeholder="+91 98765 43210"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>

                        {/* Select Your Plan (Moved alongside Referral Code) */}
                        <div className="col-lg-6">
                          <div className="xb-form-input mb-25">
                            <label>Select Your Plan <span style={{ color: "#00ff97" }}>*</span></label>
                            <select
                              name="plan"
                              value={formData.plan}
                              onChange={(e) => {
                                const newPlan = e.target.value;
                                const basePlanId = getBasePlanId(newPlan);
                                const maxAddons = Math.max(0, (TIER_VERTICAL_LIMITS[basePlanId] ?? 1) - 1);
                                const trimmedAddons = formData.addon_verticals.slice(0, maxAddons);
                                setFormData(prev => ({
                                  ...prev,
                                  plan: newPlan,
                                  addon_verticals: trimmedAddons
                                }));
                              }}
                            >
                              <option value="explorer">Explorer — Free Forever (1 Industry)</option>
                              {paidPlanOptions.map((opt) => (
                                <option
                                  key={opt.value}
                                  value={opt.value}
                                  disabled={!paidPlansEnabled}
                                >
                                  {opt.label}
                                  {!paidPlansEnabled
                                    ? " — COMING SOON"
                                    : paymentTestMode
                                    ? " — TEST MODE"
                                    : ""}
                                </option>
                              ))}
                              <option value="" disabled>Titan Enterprise — Contact Sales</option>
                            </select>
                            <p className="mt-5 mb-0" style={{ fontSize: "11px", opacity: 0.6 }}>
                              Live pricing synchronized from platform server
                            </p>
                          </div>
                        </div>

                        {/* Referral Code (Alongside Plan) */}
                        <div className="col-lg-6">
                          <div className="xb-form-input mb-25">
                            <label>Referral Code <span style={{ opacity: 0.6 }}>(Optional)</span></label>
                            <input
                              type="text"
                              name="referral_code"
                              placeholder="HYPER-XXXXX-XXXX"
                              value={formData.referral_code}
                              onChange={(e) => {
                                const value = e.target.value.toUpperCase();
                                setFormData(prev => ({ ...prev, referral_code: value }));
                                if (value.length >= 5) {
                                  validateReferralCode(value).then((result) => {
                                    if (result.valid && result.partner_name) {
                                      setReferrerName(result.partner_name);
                                    } else {
                                      setReferrerName("");
                                    }
                                  });
                                } else {
                                  setReferrerName("");
                                }
                              }}
                              style={{ textTransform: "uppercase" }}
                            />
                            {referrerName ? (
                              <p className="mt-5 mb-0" style={{ fontSize: "12px", color: "var(--color-primary)" }}>
                                <i className="fas fa-check-circle me-1"></i>
                                Referred by: <strong>{referrerName}</strong>
                              </p>
                            ) : (
                              <p className="mt-5 mb-0" style={{ fontSize: "11px", opacity: 0.6 }}>
                                Have a partner referral code? Enter it here
                              </p>
                            )}
                          </div>
                        </div>

                        {/* LEFT COLUMN: Your Industry Configuration (Primary + Additional Industries) */}
                        <div className="col-lg-6">
                          <h5 className="clr-white mb-20" style={{ borderBottom: "1px solid rgba(0, 255, 151, 0.2)", paddingBottom: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
                            <i className="fa fa-briefcase" style={{ color: "#00ff97" }}></i>
                            Your Industry Configuration
                          </h5>

                          {/* Primary Industry */}
                          <div className="xb-form-input mb-25">
                            <label>Primary Industry <span style={{ color: "#00ff97" }}>*</span></label>
                            <select
                              name="primary_vertical"
                              value={formData.primary_vertical}
                              onChange={handleChange}
                            >
                              {INDUSTRY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            <p className="mt-8 mb-0" style={{ fontSize: "12px", opacity: 0.7 }}>
                              Your core dashboard workflows will be pre-configured for this industry
                            </p>
                            {prefilledVerticalLabel && (
                              <p className="mt-8 mb-0" style={{ fontSize: "12px", color: "var(--color-primary)" }}>
                                <i className="fas fa-wand-magic-sparkles me-1"></i>
                                Picked from the {prefilledVerticalLabel} pages you were reading — change it if that's not you.
                              </p>
                            )}
                            {arrivedFromPG && (
                              <p
                                className="mt-8 mb-0"
                                style={{
                                  fontSize: "12px",
                                  lineHeight: 1.6,
                                  padding: "10px 12px",
                                  borderRadius: "8px",
                                  background: "rgba(0, 255, 151, 0.08)",
                                  border: "1px solid rgba(0, 255, 151, 0.25)",
                                }}
                              >
                                <i className="fas fa-circle-info me-2" style={{ color: "#00ff97" }}></i>
                                Running a PG, hostel or co-living? Keep <strong>Hotel &amp; Hospitality</strong> selected —
                                you'll choose <strong>PG / Co-living</strong> in the setup wizard.
                              </p>
                            )}
                          </div>

                          {/* Additional Industries (Hybrid List + Multi-Select) under Primary */}
                          <div className="xb-form-input mb-25">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                              <label style={{ margin: 0 }}>
                                Additional Industries
                              </label>
                              <span style={{ fontSize: "12px", color: "var(--color-primary)", fontWeight: 500 }}>
                                {formData.addon_verticals.length} / {Math.max(0, (TIER_VERTICAL_LIMITS[getBasePlanId(formData.plan)] ?? 1) - 1)} slots used
                              </span>
                            </div>

                            {/* Active Selected List (Chips Tray) */}
                            {formData.addon_verticals.length > 0 ? (
                              <div style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "6px",
                                marginBottom: "8px",
                                padding: "8px 10px",
                                background: "rgba(0, 255, 151, 0.06)",
                                border: "1px solid rgba(0, 255, 151, 0.25)",
                                borderRadius: "8px",
                                minHeight: "42px",
                                alignItems: "center"
                              }}>
                                <span style={{ fontSize: "11px", color: "#00ff97", fontWeight: 600, marginRight: "4px" }}>
                                  ACTIVE:
                                </span>
                                {formData.addon_verticals.map((val) => {
                                  const opt = INDUSTRY_OPTIONS.find(o => o.value === val);
                                  return (
                                    <span
                                      key={val}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "3px 10px",
                                        background: "rgba(0, 255, 151, 0.2)",
                                        border: "1px solid rgba(0, 255, 151, 0.4)",
                                        borderRadius: "14px",
                                        fontSize: "12px",
                                        color: "#ffffff",
                                        fontWeight: 500,
                                      }}
                                    >
                                      <i className={`fas ${opt?.icon || 'fa-tag'}`} style={{ color: "#00ff97", fontSize: "11px" }}></i>
                                      {opt?.label.split(' ')[0] || val}
                                      <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({
                                          ...prev,
                                          addon_verticals: prev.addon_verticals.filter(v => v !== val)
                                        }))}
                                        style={{
                                          background: "none",
                                          border: "none",
                                          color: "#fff",
                                          opacity: 0.8,
                                          padding: "0 0 0 4px",
                                          fontSize: "14px",
                                          cursor: "pointer",
                                          lineHeight: 1
                                        }}
                                        title={`Remove ${opt?.label}`}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{
                                padding: "8px 12px",
                                marginBottom: "8px",
                                background: "rgba(255, 255, 255, 0.02)",
                                border: "1px dashed rgba(255, 255, 255, 0.15)",
                                borderRadius: "8px",
                                fontSize: "12px",
                                color: "rgba(255, 255, 255, 0.55)",
                              }}>
                                {Math.max(0, (TIER_VERTICAL_LIMITS[getBasePlanId(formData.plan)] ?? 1) - 1) > 0
                                  ? `No additional industries added yet. Select up to ${Math.max(0, (TIER_VERTICAL_LIMITS[getBasePlanId(formData.plan)] ?? 1) - 1)} from the list below.`
                                  : "Explorer includes 1 primary industry. Upgrade to Officer or Commander to add more."}
                              </div>
                            )}

                            {/* Search Filter Box */}
                            <div style={{ position: "relative", marginBottom: "8px" }}>
                              <input
                                type="text"
                                placeholder="Search industries (e.g. Healthcare, Gym, Retail)..."
                                value={verticalSearch}
                                onChange={(e) => setVerticalSearch(e.target.value)}
                                style={{
                                  height: "38px !important",
                                  lineHeight: "38px !important",
                                  padding: "0 12px 0 32px !important",
                                  fontSize: "12px !important",
                                  background: "rgba(10, 25, 47, 0.6) !important",
                                }}
                              />
                              <i
                                className="fas fa-search"
                                style={{
                                  position: "absolute",
                                  left: "12px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  color: "rgba(255, 255, 255, 0.4)",
                                  fontSize: "11px",
                                  pointerEvents: "none"
                                }}
                              ></i>
                              {verticalSearch && (
                                <button
                                  type="button"
                                  onClick={() => setVerticalSearch("")}
                                  style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "none",
                                    border: "none",
                                    color: "rgba(255, 255, 255, 0.6)",
                                    fontSize: "14px",
                                    cursor: "pointer"
                                  }}
                                >
                                  ×
                                </button>
                              )}
                            </div>

                            {/* Scrollable Catalog Multi-Selection */}
                            <div
                              className="custom-vertical-scroll"
                              data-lenis-prevent
                              style={{
                                maxHeight: "185px",
                                overflowY: "auto",
                                overscrollBehavior: "contain",
                                background: "rgba(10, 25, 47, 0.7)",
                                border: "1px solid rgba(0, 255, 151, 0.2)",
                                borderRadius: "8px",
                                padding: "6px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px"
                              }}
                            >
                              {filteredVerticals.length === 0 ? (
                                <div style={{ padding: "16px", textAlign: "center", fontSize: "12px", color: "rgba(255, 255, 255, 0.5)" }}>
                                  No matching industries found for "{verticalSearch}"
                                </div>
                              ) : (
                                filteredVerticals.map((opt) => {
                                  const isSelected = formData.addon_verticals.includes(opt.value);
                                  const maxAddons = Math.max(0, (TIER_VERTICAL_LIMITS[getBasePlanId(formData.plan)] ?? 1) - 1);
                                  const isDisabled = !isSelected && formData.addon_verticals.length >= maxAddons;

                                  return (
                                    <div
                                      key={opt.value}
                                      onClick={() => {
                                        if (isDisabled) return;
                                        if (isSelected) {
                                          setFormData(prev => ({
                                            ...prev,
                                            addon_verticals: prev.addon_verticals.filter(v => v !== opt.value)
                                          }));
                                        } else {
                                          setFormData(prev => ({
                                            ...prev,
                                            addon_verticals: [...prev.addon_verticals, opt.value]
                                          }));
                                        }
                                      }}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: "8px 12px",
                                        borderRadius: "6px",
                                        background: isSelected
                                          ? "rgba(0, 255, 151, 0.12)"
                                          : "rgba(255, 255, 255, 0.02)",
                                        border: isSelected
                                          ? "1px solid rgba(0, 255, 151, 0.4)"
                                          : "1px solid transparent",
                                        cursor: isDisabled ? "not-allowed" : "pointer",
                                        opacity: isDisabled ? 0.35 : 1,
                                        transition: "all 0.15s ease",
                                      }}
                                    >
                                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <div style={{
                                          width: "18px",
                                          height: "18px",
                                          borderRadius: "4px",
                                          border: isSelected ? "1px solid #00ff97" : "1px solid rgba(255, 255, 255, 0.3)",
                                          background: isSelected ? "#00ff97" : "rgba(0, 0, 0, 0.3)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "#000",
                                          fontSize: "11px",
                                          fontWeight: "bold",
                                          flexShrink: 0
                                        }}>
                                          {isSelected && "✓"}
                                        </div>
                                        <i className={`fas ${opt.icon}`} style={{ fontSize: "12px", color: isSelected ? "#00ff97" : "rgba(255, 255, 255, 0.6)", width: "16px", textAlign: "center" }}></i>
                                        <span style={{ fontSize: "13px", color: isSelected ? "#ffffff" : "rgba(255, 255, 255, 0.85)", fontWeight: isSelected ? 600 : 400 }}>
                                          {opt.label}
                                        </span>
                                      </div>
                                      <span style={{
                                        fontSize: "11px",
                                        color: isSelected ? "#00ff97" : isDisabled ? "#ff9f43" : "rgba(255, 255, 255, 0.4)",
                                        fontWeight: 500
                                      }}>
                                        {isSelected ? "Active ✓" : isDisabled ? "Limit Reached" : "+ Add"}
                                      </span>
                                    </div>
                                  );
                                })
                              )}
                            </div>

                            {formData.addon_verticals.length > 0 && formData.plan !== "explorer" && (
                              <p className="mt-8 mb-0" style={{ fontSize: "12px", color: "var(--color-primary)" }}>
                                <i className="fas fa-tag me-1"></i>
                                +₹{(verticalMonthlyPrice ?? 0).toLocaleString('en-IN')}/vertical/mo
                              </p>
                            )}
                            {Math.max(0, (TIER_VERTICAL_LIMITS[getBasePlanId(formData.plan)] ?? 1) - 1) <= 0 && (
                              <p className="mt-8 mb-0" style={{ fontSize: "12px", color: "#ff9f43" }}>
                                <i className="fas fa-info-circle me-1"></i>
                                Upgrade to Officer or Commander above to enable additional industry add-ons
                              </p>
                            )}
                          </div>
                        </div>

                        {/* RIGHT COLUMN: Business Address alongside Your Industry */}
                        <div className="col-lg-6">
                          <h5 className="clr-white mb-20" style={{ borderBottom: "1px solid rgba(0, 255, 151, 0.2)", paddingBottom: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
                            <i className="fa fa-map-marker-alt" style={{ color: "#00ff97" }}></i>
                            Business Address
                          </h5>

                          {/* Address Line 1 (Mandatory with Autocomplete) */}
                          <div className="xb-form-input mb-25">
                            <label htmlFor="address-autocomplete">
                              Address Line 1 <span style={{ color: "#00ff97" }}>*</span>
                            </label>
                            <div ref={addressBoxRef} style={{ position: "relative" }}>
                              <input
                                type="text"
                                name="address_line"
                                placeholder="Building, street, area..."
                                value={formData.address_line}
                                onChange={handleChange}
                                onKeyDown={handleAddressKeyDown}
                                onFocus={() => { if (addressSuggestions.length > 0) setAddressOpen(true); }}
                                id="address-autocomplete"
                                autoComplete="off"
                                role="combobox"
                                aria-expanded={addressOpen}
                                aria-controls="address-suggestions"
                                aria-autocomplete="list"
                                aria-activedescendant={
                                  addressActive >= 0 ? `address-suggestion-${addressActive}` : undefined
                                }
                                required
                              />
                              {addressLoading && (
                                <i
                                  className="fas fa-spinner fa-spin"
                                  aria-hidden="true"
                                  style={{
                                    position: "absolute",
                                    right: "16px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#00ff97",
                                  }}
                                />
                              )}
                              {addressOpen && addressSuggestions.length > 0 && (
                                <ul
                                  id="address-suggestions"
                                  data-lenis-prevent
                                  role="listbox"
                                  aria-label="Address suggestions"
                                  style={{
                                    position: "absolute",
                                    zIndex: 20,
                                    top: "calc(100% + 4px)",
                                    left: 0,
                                    right: 0,
                                    margin: 0,
                                    padding: "6px 0",
                                    listStyle: "none",
                                    background: "linear-gradient(135deg, rgba(10, 25, 47, 0.98) 0%, rgba(15, 35, 60, 0.98) 100%)",
                                    border: "1px solid rgba(0, 255, 151, 0.35)",
                                    borderRadius: "8px",
                                    boxShadow: "0 12px 30px rgba(0, 0, 0, 0.5)",
                                    maxHeight: "240px",
                                    overflowY: "auto",
                                    overscrollBehavior: "contain",
                                  }}
                                >
                                  {addressSuggestions.map((suggestion, i) => (
                                    <li
                                      key={suggestion.label}
                                      id={`address-suggestion-${i}`}
                                      role="option"
                                      aria-selected={i === addressActive}
                                      onMouseEnter={() => setAddressActive(i)}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        applyAddressSuggestion(suggestion);
                                      }}
                                      style={{
                                        padding: "10px 16px",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        color: "#fff",
                                        background: i === addressActive ? "rgba(0, 255, 151, 0.14)" : "transparent",
                                      }}
                                    >
                                      <i
                                        className="fas fa-location-dot"
                                        aria-hidden="true"
                                        style={{ marginRight: "10px", color: "#00ff97", opacity: 0.8 }}
                                      />
                                      {suggestion.label}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            <p className="mt-5 mb-0" style={{ fontSize: "11px", opacity: 0.6 }}>
                              Pick a suggestion and we'll fill in city, state and pincode for you
                            </p>
                          </div>

                          {/* Address Line 2 (Optional) */}
                          <div className="xb-form-input mb-25">
                            <label htmlFor="address-line-2">
                              Address Line 2 <span style={{ opacity: 0.6 }}>(Optional)</span>
                            </label>
                            <input
                              type="text"
                              name="address_line_2"
                              id="address-line-2"
                              placeholder="Apartment, suite, unit, floor, landmark, etc."
                              value={formData.address_line_2 || ""}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="row">
                            {/* City */}
                            <div className="col-sm-6">
                              <div className="xb-form-input mb-25">
                                <label>City <span style={{ color: "#ff6b6b" }}>*</span></label>
                                <input
                                  type="text"
                                  name="city"
                                  placeholder="e.g. Mumbai"
                                  value={formData.city}
                                  onChange={handleChange}
                                  required
                                />
                              </div>
                            </div>

                            {/* State */}
                            <div className="col-sm-6">
                              <div className="xb-form-input mb-25">
                                <label>State / UT <span style={{ color: "#ff6b6b" }}>*</span></label>
                                <select
                                  name="state"
                                  value={formData.state}
                                  onChange={handleChange}
                                  required
                                >
                                  <option value="">Select State</option>
                                  {INDIAN_STATES.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Pincode */}
                            <div className="col-sm-6">
                              <div className="xb-form-input mb-25">
                                <label>Pincode</label>
                                <input
                                  type="text"
                                  name="pincode"
                                  placeholder="110001"
                                  value={formData.pincode}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                                    setFormData(prev => ({ ...prev, pincode: value }));
                                  }}
                                  maxLength={6}
                                />
                                <p className="mt-5 mb-0" style={{ fontSize: "11px", opacity: 0.6 }}>
                                  Auto-fills city &amp; state
                                </p>
                              </div>
                            </div>

                            {/* GSTIN */}
                            <div className="col-sm-6">
                              <div className="xb-form-input mb-25">
                                <label>
                                  GSTIN <span style={{ opacity: 0.6 }}>(Optional)</span>
                                </label>
                                <input
                                  type="text"
                                  name="gstin"
                                  placeholder="22AAAAA0000A1Z5"
                                  value={formData.gstin}
                                  onChange={(e) => {
                                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
                                    const updates: Partial<SignupFormData> = { gstin: value };
                                    if (value.length >= 2 && !formData.state) {
                                      const stateFromGst = GSTIN_STATE_MAP[value.substring(0, 2)];
                                      if (stateFromGst) updates.state = stateFromGst;
                                    }
                                    setFormData(prev => ({ ...prev, ...updates }));
                                  }}
                                  maxLength={15}
                                  style={{ textTransform: "uppercase" }}
                                />
                                <p className="mt-5 mb-0" style={{ fontSize: "11px", opacity: 0.6 }}>
                                  {formData.gstin.length > 0 ? `${formData.gstin.length}/15` : "Auto-fills state code"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="xb-form-input mb-25">
                            {/* The plan <select> itself lives once, above, next
                                to Referral Code — this block used to render a
                                second one here before formData.plan was moved
                                up alongside it. The order-summary panel below
                                still reads formData.plan and belongs here,
                                right before Continue to Checkout. */}
                            {planDowngradeNotice && (
                              <div
                                className="mt-15"
                                style={{
                                  padding: "12px 15px",
                                  background: "rgba(0, 174, 255, 0.10)",
                                  border: "1px solid rgba(0, 174, 255, 0.40)",
                                  borderRadius: "8px",
                                  color: "#8fd6ff",
                                  fontSize: "13px",
                                }}
                              >
                                {planDowngradeNotice}
                              </div>
                            )}
                            {paidPlansEnabled && saleUnlocked && (
                              <div
                                className="mt-15"
                                style={{
                                  padding: "12px 15px",
                                  background: "rgba(160, 120, 255, 0.12)",
                                  border: "1px solid rgba(160, 120, 255, 0.45)",
                                  borderRadius: "8px",
                                  color: "#c9b3ff",
                                  fontSize: "13px",
                                }}
                              >
                                <strong>PRE-LAUNCH ACCESS</strong> — paid plans are open to you
                                via an unlock link. The public still sees them as coming soon.
                              </div>
                            )}
                            {paidPlansEnabled && paymentTestMode && (
                              <div
                                className="mt-15"
                                style={{
                                  padding: "12px 15px",
                                  background: "rgba(255, 170, 0, 0.12)",
                                  border: "1px solid rgba(255, 170, 0, 0.45)",
                                  borderRadius: "8px",
                                  color: "#ffc861",
                                  fontSize: "13px",
                                }}
                              >
                                <strong>TEST MODE</strong> — payments run against a Razorpay
                                test key. No money is charged, no invoice is raised and no
                                accounting entry is posted. The tenant is provisioned for real.
                              </div>
                            )}
                            {formData.plan !== "explorer" && (() => {
                              // Base price and billing period, read live from
                              // pricingTiers/PAID_PLAN_SHAPES instead of a
                              // frozen dict — this used to hardcode Officer at
                              // ₹4,999 and Commander at ₹14,999 while the
                              // Mothership charged ₹1,500/₹3,600, and never
                              // had a Titan entry at all so Titan skipped this
                              // panel entirely. Same failure class as
                              // PAID_PLAN_SHAPES above (BR-101): a frozen
                              // number the customer never actually pays.
                              const shape = PAID_PLAN_SHAPES.find((s) => s.value === formData.plan);
                              const tier = pricingTiers.find((t) => t.id === shape?.tier);
                              const priceMap: Record<string, number | null | undefined> = tier
                                ? {
                                    monthly: tier.monthlyPrice,
                                    quarterly: tier.quarterlyPrice,
                                    sixMonth: tier.sixMonthPrice,
                                    yearly: tier.yearlyPrice,
                                  }
                                : {};
                              const rawBasePrice = shape ? priceMap[shape.cycle] : null;
                              const basePrice = typeof rawBasePrice === "number" ? rawBasePrice : 0;

                              // Multiply the monthly vertical price by however many
                              // months this billing cycle actually covers.
                              const cycleMonths: Record<string, number> = {
                                monthly: 1,
                                quarterly: 3,
                                sixMonth: 6,
                                yearly: 12,
                              };
                              const verticalMultiplier = shape ? (cycleMonths[shape.cycle] || 1) : 1;
                              const verticalAddonCost = formData.addon_verticals.length * (verticalMonthlyPrice ?? 0);
                              const totalVerticalCost = verticalAddonCost * verticalMultiplier;

                              // Calculate subtotal and GST
                              const subtotal = basePrice + totalVerticalCost;
                              const gstAmount = Math.round(subtotal * 0.18);
                              const grandTotal = subtotal + gstAmount;

                              const billingPeriod = shape ? shape.suffix : "/mo";

                              // No price yet (still loading, tier not found, or the
                              // API is down) — say so instead of showing a breakdown
                              // built on a silent ₹0 base price.
                              if (!shape || rawBasePrice == null) {
                                return null;
                              }

                              return (
                                <div style={{
                                  padding: "18px 20px",
                                  background: "linear-gradient(135deg, rgba(0, 255, 151, 0.08) 0%, rgba(10, 25, 47, 0.95) 100%)",
                                  border: "1px solid rgba(0, 255, 151, 0.35)",
                                  borderRadius: "10px",
                                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.35)"
                                }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", borderBottom: "1px solid rgba(255, 255, 255, 0.1)", paddingBottom: "8px" }}>
                                    <h6 className="clr-white" style={{ margin: 0, fontSize: "14px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                      <i className="fas fa-receipt me-2" style={{ color: "#00ff97" }}></i>
                                      Order Summary
                                    </h6>
                                    <span style={{ fontSize: "12px", color: "#00ff97", fontWeight: 600 }}>
                                      {formData.plan.toUpperCase().replace(/_/g, " ")}
                                    </span>
                                  </div>
                                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                                    <span className="clr-white" style={{ fontSize: "13px", opacity: 0.85 }}>Base Plan ({formData.plan.replace(/_/g, ' ')})</span>
                                    <span className="clr-white" style={{ fontSize: "13px", fontWeight: 500 }}>₹{basePrice.toLocaleString('en-IN')}{billingPeriod}</span>
                                  </div>
                                  {formData.addon_verticals.length > 0 && (
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                                      <span style={{ fontSize: "13px", color: "var(--color-primary)" }}>
                                        + {formData.addon_verticals.length} Addon Vertical{formData.addon_verticals.length > 1 ? 's' : ''}
                                      </span>
                                      <span style={{ fontSize: "13px", color: "var(--color-primary)", fontWeight: 500 }}>
                                        ₹{totalVerticalCost.toLocaleString('en-IN')}{billingPeriod}
                                      </span>
                                    </div>
                                  )}
                                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                                    <span className="clr-white" style={{ fontSize: "13px", opacity: 0.85 }}>Subtotal</span>
                                    <span className="clr-white" style={{ fontSize: "13px" }}>₹{subtotal.toLocaleString('en-IN')}{billingPeriod}</span>
                                  </div>
                                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                                    <span style={{ fontSize: "13px", color: "#ffc800" }}>GST (18%)</span>
                                    <span style={{ fontSize: "13px", color: "#ffc800" }}>₹{gstAmount.toLocaleString('en-IN')}</span>
                                  </div>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid rgba(0, 255, 151, 0.3)", paddingTop: "10px", marginTop: "6px" }}>
                                    <span style={{ fontSize: "15px", color: "#ffffff", fontWeight: 600 }}>Total Due</span>
                                    <span style={{ fontSize: "20px", color: "var(--color-primary)", fontWeight: "bold" }}>
                                      ₹{grandTotal.toLocaleString('en-IN')}<span style={{ fontSize: "13px", fontWeight: 400, opacity: 0.85 }}>{billingPeriod}</span>
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Submit Button */}
                        <div className="col-12 mt-10">
                          {/* Disabled with no explanation reads as broken, not
                              gated — spell out exactly what's missing so the
                              user can fix it instead of clicking a dead
                              button. Only one reason shows at a time, in the
                              same priority the disabled= check uses. */}
                          {!loading && (subdomainAvailable === false || !emailVerified) && (
                            <div
                              className="mb-10"
                              style={{
                                padding: "10px 15px",
                                background: "rgba(255, 170, 0, 0.12)",
                                border: "1px solid rgba(255, 170, 0, 0.45)",
                                borderRadius: "8px",
                                color: "#ffc861",
                                fontSize: "13px",
                              }}
                            >
                              <i className="fas fa-triangle-exclamation me-2"></i>
                              {subdomainAvailable === false
                                ? (subdomainIsPending
                                    ? "This subdomain is reserved by a pending registration. Click 'Complete Setup' above to complete registration."
                                    : "Choose a different subdomain before continuing — the one above is already taken.")
                                : "Verify your email address above before continuing."}
                            </div>
                          )}
                          {subdomainIsPending ? (
                            <button
                              type="button"
                              onClick={handleRequestResume}
                              disabled={resumeLoading}
                              className="thm-btn agency-btn w-100"
                              style={{
                                justifyContent: "center",
                                height: "52px",
                                background: "linear-gradient(90deg, #06b6d4, #00ff97)",
                                color: "#00020f",
                                fontWeight: 700
                              }}
                            >
                              <span className="text">
                                {resumeLoading ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin" style={{ marginRight: "10px" }}></i>
                                    Loading Setup...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-arrow-right" style={{ marginRight: "10px" }}></i>
                                    Complete Setup & Pay for {formData.subdomain}.axoix.io &rarr;
                                  </>
                                )}
                              </span>
                            </button>
                          ) : (
                            <button
                              type="submit"
                              className="thm-btn agency-btn w-100"
                              disabled={loading || subdomainAvailable === false || !emailVerified}
                              style={{ justifyContent: "center", height: "52px" }}
                            >
                              <span className="text">
                                {loading ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin" style={{ marginRight: "10px" }}></i>
                                    Processing...
                                  </>
                                ) : (
                                  'Continue to Checkout'
                                )}
                              </span>
                              {!loading && (
                                <span className="arrow">
                                  <span className="arrow-icon">
                                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white"/>
                                      <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white"/>
                                      <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white"/>
                                      <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white"/>
                                      <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white"/>
                                      <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white"/>
                                      <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white"/>
                                    </svg>
                                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white"/>
                                      <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white"/>
                                      <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white"/>
                                      <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white"/>
                                      <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white"/>
                                      <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white"/>
                                      <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white"/>
                                    </svg>
                                  </span>
                                </span>
                              )}
                            </button>
                          )}
                        </div>

                        {/* Legal & Terms Notice */}
                        <div className="col-12 mt-20 text-center">
                          <p className="clr-white" style={{ fontSize: "13px" }}>
                            By signing up, you agree to our{' '}
                            <Link to="/terms" style={{ color: "var(--color-primary)" }}>Terms of Service</Link> and{' '}
                            <Link to="/privacy" style={{ color: "var(--color-primary)" }}>Privacy Policy</Link>.
                          </p>
                          <p className="clr-white mt-5" style={{ fontSize: "12px", opacity: 0.7 }}>
                            Free Explorer plan includes 14 days of extended features.
                          </p>
                        </div>
                      </div>
                    </form>

                    <div className="text-center mt-30">
                      <p className="clr-white">
                        Already have an account?{' '}
                        <a
                          href={formData.subdomain ? `https://${formData.subdomain}.axoix.io/login` : '#'}
                          onClick={(e) => {
                            if (!formData.subdomain) {
                              e.preventDefault();
                              alert('Please enter your subdomain first, then click Log in.');
                            }
                          }}
                          style={{ color: "var(--color-primary)" }}
                        >
                          Log in to {formData.subdomain ? `${formData.subdomain}.axoix.io` : 'your dashboard'}
                        </a>
                      </p>
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default SignupPage;
